import React, { useCallback, useContext, useEffect, useState, useRef, useMemo } from 'react';
import {
  Typography,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import { useAptorApi } from '../../../../../Api';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { useIntl } from 'react-intl';
import { IProps, StepData, EditMode } from './types';
import Step1Choice from './Step1Choice';
import Step2BasicInfo from './Step2BasicInfo';
import Step3ShortDescription from './Step3ShortDescription';
import Step4Area from './Step4Area';
import Step5CompanyUnit from './Step5CompanyUnit';
import Step6EffectiveDates from './Step6EffectiveDates';
import Step7EffectiveToSendoutTime from './Step7EffectiveToSendoutTime';
import Step7Responsible from './Step7Responsible';
import Step8Preview from './Step8Preview';
import DeleteStep1CompanyUnit from './DeleteStep1CompanyUnit';
import DeleteStep2RequirementsTable from './DeleteStep2RequirementsTable';
import DeleteStep3Preview from './DeleteStep3Preview';
import EditStep1SelectRequirement from './EditStep1SelectRequirement';
import { useSnackbar } from 'notistack';
import { FormValues } from '../../../../../Components/Form/Form.types';
import { EvaluationInfoEnum } from '../../../../../Context/EvaluationInfoEmun';

const PRIMARY = '#c4122f';

interface IOptions {
  areas: { id: number; name: string }[];
  companyUnits: { id: number; name: string }[];
  complianceUsers: { id: number; name: string }[];
}

const OtherRequirementWizard: React.FC<IProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  const { api, abortController } = useAptorApi();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [editMode, setEditMode] = useState<EditMode>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [stepData, setStepData] = useState<StepData>({});
  const [options, setOptions] = useState<IOptions | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [otherRequirementInfo, setOtherRequirementInfo] = useState<any>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedCompanyUnit, setSelectedCompanyUnit] = useState<any>(null);
  const sessionHydrated = useRef(false);

  const DEFAULT_STEP_INFO = useMemo(() => ({
    'choice': {
      otherRequirementInformationSv: '',
      otherRequirementInformationEn: '',
    },
    'name': {
      otherRequirementInformationSv: '',
      otherRequirementInformationEn: '',
    },
    'shortDescription': {
      otherRequirementInformationSv: '',
      otherRequirementInformationEn: '',
    },
    'area': {
      otherRequirementInformationSv: '',
      otherRequirementInformationEn: '',
    },
    'companyUnit': {
      otherRequirementInformationSv: '',
      otherRequirementInformationEn: '',
    },
    'Dates': {
      otherRequirementInformationSv: '',
      otherRequirementInformationEn: '',
    },
    'effectiveToSendoutTime': {
      otherRequirementInformationSv: '',
      otherRequirementInformationEn: '',
    },
    'responsible': {
      otherRequirementInformationSv: '',
      otherRequirementInformationEn: '',
    },
    'selectRequirement': {
      otherRequirementInformationSv: '',
      otherRequirementInformationEn: '',
    },
    'deleteCompanyUnit': {
      otherRequirementInformationSv: '',
      otherRequirementInformationEn: '',
    },
  }), []);

  useEffect(() => {
    if (!options) {
      Promise.all([
        api.getCustomerLawSettings('areas'),
        api.getCustomerCompanyUnits(),
        api.getCustomerUsers(),
      ]).then(([areas, units, users]) => {
        if (abortController.current.signal.aborted) {
          return;
        }
        setOptions({
          areas: areas.items,
          companyUnits: units.items,
          complianceUsers: users.items,
        });
      });
    }
  }, [api, abortController, options]);
  useEffect(() => {
    if (open && !sessionHydrated.current) {
      setCurrentStep(1);
      setEditMode(null);
      setStepData({});
      sessionHydrated.current = true;
    }
  }, [open]);

  // Clear selected rows when company unit changes in delete mode
  useEffect(() => {
    if (editMode === 'delete' && selectedCompanyUnit) {
      setSelectedRows([]);
    }
  }, [selectedCompanyUnit, editMode]);

  // Auto-clear effectiveToSendoutTime when either date is cleared
  useEffect(() => {
    if (!stepData.effectiveFrom || !stepData.effectiveTo) {
      setStepData((prev) => ({ ...prev, effectiveToSendoutTime: undefined }));
    }
  }, [stepData.effectiveFrom, stepData.effectiveTo]);
  
  const fetchOtherReqStepInfo = React.useCallback(() => {
    api
      .getLawportalStepSettings(EvaluationInfoEnum.otherRequirementInfo)
      .then((result) => {
        if (abortController.current.signal.aborted) return;
        if (result) {
          try {
            const parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
            setOtherRequirementInfo(parsedResult);
          } catch (error) {
            console.error('Error parsing evaluation step info:', error);
            setOtherRequirementInfo(DEFAULT_STEP_INFO);
          }
        } else {
          setOtherRequirementInfo(DEFAULT_STEP_INFO);
        }
      })
      .catch((err) => {
        console.error('API error fetching step settings:', err);
        setOtherRequirementInfo(DEFAULT_STEP_INFO);
      });
  }, [api, abortController, DEFAULT_STEP_INFO]);

    const handleOtherReqStepInfoUpdate = React.useCallback(() => {
      fetchOtherReqStepInfo();
    }, [fetchOtherReqStepInfo]);
  
    useEffect(() => {
      fetchOtherReqStepInfo();
    }, [fetchOtherReqStepInfo]);

  const handleModeSelect = (mode: EditMode) => {
    if (mode !== editMode) {
      setStepData({});
    }
    setEditMode(mode);
    if (mode === 'add') {
      setCurrentStep(2);
    } else if (mode === 'edit' || mode === 'delete') {
      setCurrentStep(2);
    }
  };

  const handleStepNext = useCallback((data: Partial<StepData>) => {
    setStepData((prev) => ({ ...prev, ...data }));
    setCurrentStep((prev) => prev + 1);
  }, []);

  const handleStepBack = useCallback(() => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setSelectedRows([]);
      setSelectedCompanyUnit(null);
    } else {
      setCurrentStep((prev) => Math.max(2, prev - 1));
    }
  }, [currentStep]);

  const handleClose = () => {
    setCurrentStep(1);
    setEditMode(null);
    setStepData({});
    setSelectedRows([]);
    setSelectedCompanyUnit(null);
    sessionHydrated.current = false;
    onClose();
  };

  const handleConfirmAdd = async () => {
    try {
      setIsLoading(true);

      const formData: FormValues = {
        name: stepData.name || '',
        description: stepData.shortDescription || '',
        area: stepData.area || 0,
        effectiveFrom: stepData.effectiveFrom || '',
        effectiveTo: stepData.effectiveTo || undefined,
        effectiveToSendoutTime: stepData.effectiveToSendoutTime || undefined,
      } as any;

      if (editMode === 'add') {
        Object.assign(formData, {
          unitIds: stepData.unitIds?.map((u) => u.id) || [],
        });
      }

      if (stepData.hasReminder && stepData.responsible && stepData.responsible.length > 0) {
        Object.assign(formData, {
          complianceUsers: stepData.responsible.map((u) => u.value),
        });
      }

      let ok;
      if (editMode === 'edit') {
        const selectedReq = (stepData as any).selectedRequirement;
        await api.editCustomerRequirement(selectedReq?.id, formData);
        ok = true;
      } else {
        await api.createCustomerRequirement(formData);
        ok = true;
      }

      if (!ok && !abortController.current.signal.aborted) {
        enqueueSnackbar(
          formatMessage({
            id: 'law-portal.manage.requirements.error',
            defaultMessage: editMode === 'edit' ? 'Failed to update requirement' : 'Failed to create requirement',
          }),
          { variant: 'error' },
        );
      } else {
        enqueueSnackbar(
          formatMessage({
            id: 'law-portal.manage.requirements.success',
            defaultMessage: editMode === 'edit' ? 'Requirement updated successfully!' : 'Requirement created successfully!',
          }),
          { variant: 'success' },
        );
        handleClose();
        onComplete?.();
      }
    } catch (error) {
      enqueueSnackbar(
        formatMessage({
          id: 'law-portal.manage.requirements.error',
          defaultMessage: editMode === 'edit' ? 'Failed to update requirement' : 'Failed to create requirement',
        }),
        { variant: 'error' },
      );
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setIsLoading(true);

      if (selectedRows.length === 0) {
        enqueueSnackbar(
          formatMessage({
            id: 'law-portal.manage.requirements.selectRequirements',
            defaultMessage: 'Please select requirements to delete',
          }),
          { variant: 'warning' },
        );
        setIsLoading(false);
        return;
      }

      for (const reqId of selectedRows) {
        await api.removeCustomerRequirement(reqId);
        if (abortController.current.signal.aborted) {
          return;
        }
      }

      enqueueSnackbar(
        formatMessage({
          id: 'law-portal.manage.requirements.deleteSuccess',
          defaultMessage: 'Requirements deleted successfully!',
        }),
        { variant: 'success' },
      );
      handleClose();
      onComplete?.();
    } catch (error) {
      enqueueSnackbar(
        formatMessage({
          id: 'law-portal.manage.requirements.deleteError',
          defaultMessage: 'Failed to delete requirements',
        }),
        { variant: 'error' },
      );
      setIsLoading(false);
    }
  };

  const getTotalSteps = (): number => {
    if (!editMode) return 0;
    const hasBothDates = stepData.effectiveFrom && stepData.effectiveTo;
    if (editMode === 'add') {
      // ADD mode: no sendout time step
      return 8;
    }
    if (editMode === 'edit') {
      // EDIT mode: include sendout time step only if both dates are filled
      return hasBothDates ? 7 : 6;
    }
    if (editMode === 'delete') {
      return 3;
    }
    return 0;
  };
    
  const getDisplayedStep = useCallback(() => {
    if (currentStep === 1) return 0;
    return currentStep - 1;
  }, [currentStep]);

  const renderStep = () => {
    if (currentStep === 1) {
      return (
        <Step1Choice
          onModeSelect={handleModeSelect}
          onCancel={handleClose}
          currentEditMode={editMode}
          currentStepName="choice"
          otherRequirementInfo={otherRequirementInfo}
          setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
        />
      );
    }

    if (!editMode || !options) return null;

    if (editMode === 'edit') {
      switch (currentStep) {
        case 2:
          return (
            <EditStep1SelectRequirement
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              formatMessage={formatMessage}
              showBack={true}
              currentData={stepData}
            />
          );
        case 3:
          return (
            <Step2BasicInfo
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              otherRequirementInfo={otherRequirementInfo}
              setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
              currentStepName="name"
            />
          );
        case 4:
          return (
            <Step3ShortDescription
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              otherRequirementInfo={otherRequirementInfo}
              setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
              currentStepName="shortDescription"
            />
          );
        case 5:
          return (
            <Step4Area
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              areas={options.areas}
              otherRequirementInfo={otherRequirementInfo}
              setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
              currentStepName="area"
            />
          );
        case 6:
          return (
            <Step6EffectiveDates
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              otherRequirementInfo={otherRequirementInfo}
              setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
              currentStepName="Dates"
            />
          );
        case 7:
          // Conditionally show the effective to sendout time step if both dates are filled
          if (stepData.effectiveFrom && stepData.effectiveTo) {
            return (
              <Step7EffectiveToSendoutTime
                currentData={stepData}
                isFirstStep={false}
                onCancel={handleClose}
                onNext={handleStepNext}
                onBack={handleStepBack}
                otherRequirementInfo={otherRequirementInfo}
                setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
                currentStepName="effectiveToSendoutTime"
              />
            );
          } else {
            // Skip to preview if dates are not both filled
            return (
              <Step8Preview
                currentData={stepData}
                isFirstStep={false}
                onCancel={handleClose}
                onNext={handleConfirmAdd}
                onConfirmSave={handleConfirmAdd}
                onBack={handleStepBack}
                areas={options.areas}
                companyUnits={options.companyUnits}
                complianceUsers={options.complianceUsers}
                editMode={editMode}
              />
            );
          }
        case 8:
          return (
            <Step8Preview
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleConfirmAdd}
              onConfirmSave={handleConfirmAdd}
              onBack={handleStepBack}
              areas={options.areas}
              companyUnits={options.companyUnits}
              complianceUsers={options.complianceUsers}
              editMode={editMode}
            />
          );
        default:
          return null;
      }
    }

    if (editMode === 'add') {
      switch (currentStep) {
        case 2:
          return (
            <Step2BasicInfo
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              otherRequirementInfo={otherRequirementInfo}
              setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
              currentStepName="name"
            />
          );
        case 3:
          return (
            <Step3ShortDescription
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              otherRequirementInfo={otherRequirementInfo}
              setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
              currentStepName="shortDescription"
            />
          );
        case 4:
          return (
            <Step4Area
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              areas={options.areas}
              otherRequirementInfo={otherRequirementInfo}
              setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
              currentStepName="area"
            />
          );
        case 5:
          return (
            <Step5CompanyUnit
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              companyUnits={options.companyUnits}
              otherRequirementInfo={otherRequirementInfo}
              setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
              currentStepName="companyUnit"
            />
          );
        case 6:
          return (
            <Step6EffectiveDates
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              otherRequirementInfo={otherRequirementInfo}
              setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
              currentStepName="Dates"
            />
          );
        case 7:
          // In ADD mode, skip effectiveToSendoutTime step and go directly to responsible
          return (
            <Step7Responsible
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              complianceUsers={options.complianceUsers}
              otherRequirementInfo={otherRequirementInfo}
              setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
              currentStepName="responsible"
            />
          );
        case 8:
          return (
            <Step8Preview
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              onConfirmSave={handleConfirmAdd}
              areas={options.areas}
              companyUnits={options.companyUnits}
              complianceUsers={options.complianceUsers}
            />
          );
        default:
          return null;
      }
    }

    if (editMode === 'delete') {
      switch (currentStep) {
        case 2:
          return (
            <DeleteStep1CompanyUnit
              currentData={stepData}
              isFirstStep={false}
              onCancel={handleClose}
              onNext={handleStepNext}
              onBack={handleStepBack}
              companyUnits={options.companyUnits}
              onCompanyUnitSelect={setSelectedCompanyUnit}
              currentStepName="deleteCompanyUnit"
              otherRequirementInfo={otherRequirementInfo}
              setOtherRequirementInfo={handleOtherReqStepInfoUpdate}
            />
          );
        case 3:
          return (
            <DeleteStep2RequirementsTable
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              selectedCompanyUnit={selectedCompanyUnit}
              onNext={(data: any) => {
                setStepData((prev) => ({ ...prev, ...data }));
                setCurrentStep((prev) => prev + 1);
              }}
              onBack={handleStepBack}
              onCancel={handleClose}
              formatMessage={formatMessage}
              showBack={true}
            />
          );
        case 4:
          return (
            <DeleteStep3Preview
              selectedRequirements={stepData.selectedRequirements || []}
              selectedCompanyUnit={selectedCompanyUnit}
              onBack={handleStepBack}
              onCancel={handleClose}
              onConfirmDelete={handleConfirmDelete}
            />
          );
        default:
          return null;
      }
    }

    return null;
  };

  const totalSteps = useMemo(() => getTotalSteps(), [editMode, stepData.hasReminder, stepData.effectiveFrom, stepData.effectiveTo]);
  const displayedStep = useMemo(() => getDisplayedStep(), [getDisplayedStep]);

  return (
    <>
      <Dialog open={open} maxWidth="lg" fullWidth disableEscapeKeyDown>
        <DialogTitle
          style={{
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {formatMessage({
              id: 'law-portal.manage.other-requiremnt.editOtherRequirement',
            })}
          </Typography>
        </DialogTitle>

        <DialogContent style={{ paddingTop: '24px', minHeight: '400px' }}>
          <Box style={{ minHeight: '420px' }}>
            {currentStep > 1 && (
              <Box
                style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '16px',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box style={{width:'100%', display: 'flex', gap: '8px',justifyContent:'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    style={{ borderColor: PRIMARY, color: PRIMARY }}
                  >
                    {formatMessage({ id: 'form.cancel' })}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleStepBack}
                    style={{ borderColor: PRIMARY, color: PRIMARY }}
                  >
                    {formatMessage({ id: 'component.back-button.back', defaultMessage: 'Back' })}
                  </Button>
                </Box>
              </Box>
            )}

            {currentStep > 1 && editMode && (
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 16px',
                  marginBottom: '12px',
                  backgroundColor: '#fdf2f3',
                  borderRadius: '6px',
                  borderLeft: `4px solid ${PRIMARY}`,
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: 700,
                    color: PRIMARY,
                    fontSize: '13px',
                  }}
                >
                  {formatMessage(
                    { id: 'law-portal.evalution.plan.step' },
                    { currentStep: displayedStep, totalSteps },
                  )}
                </Typography>

                <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  {Array(totalSteps)
                    .fill(null)
                    .map((_, idx) => (
                      <Box
                        key={idx}
                        style={{
                          width: idx === displayedStep - 1 ? '20px' : '8px',
                          height: '8px',
                          borderRadius: '4px',
                          backgroundColor:
                            idx < displayedStep ? PRIMARY : '#e0e0e0',
                          transition: 'all 0.3s ease',
                        }}
                      />
                    ))}
                </Box>
              </Box>
            )}

            {renderStep()}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OtherRequirementWizard;
